import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import DigConnectForm from "./DigConnectForm";
import dig_step_1 from "./images/dig_step_1.png";
import dig_step_2 from "./images/dig_step_2.png";
import dig_step_3 from "./images/dig_step_3.png";

const DigIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("dig");
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerDigIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerDigIntegration(values.api_token, values.company_id, values.region)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Dig Security integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will retrieve information about sensitive data assets from Dig Security."
            : ""
        }
        image={logos.DigLogo}
        name={"Dig Security"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Dig Security is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Dig Security">
                  <Typography variant={"body2"}>
                    Login to your Dig Security account
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create a new API key">
                  <Typography variant={"body2"}>
                    In the side menu, click <b>Settings</b>, and go to the{" "}
                    <b>API </b>tab.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: dig_step_1,
                        altext: "Dig Security API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Enter a meaningful name and a description for the API, and
                    click Create Key.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: dig_step_2,
                        altext: "Dig Security create API key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In the pop-up, click Copy API Key to copy the key, and click
                    Done.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: dig_step_3,
                        altext: "Dig Security API key copied",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the API key here">
                  <Box width={700}>
                    <DigConnectForm onSubmit={onSubmit} guideMode={guideMode} />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default DigIntegration;
